import { Dialog } from '@angular/cdk/dialog';
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  contentChild,
  inject,
  input,
  model,
  output,
  signal,
  TemplateRef,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthService } from '@core/authentication/auth.service';
import { AttachmentService } from '@core/services/attachment.service';
import { OiqFormDropdownComponent } from '@oiq-ui-sdk/form';
import { OIQ_APPLICATION_STIPULATIONS_PROVIDER } from '@v2/modules/applications/components/stipulations/stipulations.provider';
import { EmailAttachment } from '@v2/modules/email-app/email-app.interfaces';
import { EmailApplicationBridgeService } from '@v2/modules/email-app/services/email-application-bridge.service';
import {
  Attachment,
  EmailOAuthService,
  EmailProvider,
} from '@v2/modules/email-app/services/email-oauth.service';
import {
  EmailOcrPngFile,
  EmailOcrResult,
  EmailOcrService,
} from '@v2/modules/email-app/services/email-ocr.service';
import { EmailPreviewService } from '@v2/modules/email-app/services/email-preview.service';
import { catchError, Observable, of, tap } from 'rxjs';
import { EMAIL_OCR_PREVIEW_STATUS } from '../../email-app.models';
import { EmailLayoutService } from '../../services/email-layout.service';

@Component({
  selector: 'oiq-email-attachment',
  imports: [CommonModule, OiqFormDropdownComponent],
  templateUrl: './email-attachment.component.html',
  styleUrl: './email-attachment.component.scss',
  host: {
    class: 'flex items-center justify-between',
  },
})
export class EmailAttachmentComponent {
  private _stipulations = inject(OIQ_APPLICATION_STIPULATIONS_PROVIDER);
  private emailOcrService = inject(EmailOcrService);
  private authService = inject(AuthService);
  private emailPreviewService = inject(EmailPreviewService);
  private emailApplicationBridge = inject(EmailApplicationBridgeService);
  private emailLayoutService = inject(EmailLayoutService);
  private emailOAuthService = inject(EmailOAuthService);
  private attachmentService = inject(AttachmentService);
  public dialog: Dialog = inject(Dialog);

  checkboxTemplateRef = contentChild<TemplateRef<HTMLElement>>('checkboxTemplate');

  mode = input<'view' | 'attach'>('view');
  // TODO: Define types for displayMode input
  displayMode = input<'email' | 'dialog'>('email');
  // TODO: Define types for pngPreviewMode input
  pngPreviewMode = input<'email-preview' | 'new-application'>();
  emailAttachment = input.required<EmailAttachment>();
  emailId = input<string | null>(null);
  provider = input<EmailProvider>('outlook');
  attachedStipulations = model<
    {
      attachmentId: number;
      stipulationId: number;
    }[]
  >([]);

  ocrStatuses = input.required<Map<string, EMAIL_OCR_PREVIEW_STATUS>>();
  ocrPreviewRequest = output<Observable<EmailOcrResult>>();
  onCloseOcrPreview = output<string>();

  applicationStipulations = toSignal(this._stipulations.rows$, { initialValue: [] });

  ocrPreviewStatus = computed(() => {
    const attachment = this.emailAttachment();
    return this.ocrStatuses().get(attachment.id) || 'initial';
  });

  // OCR processing state
  isProcessingOcr = computed(() => this.ocrPreviewStatus() === 'loading');
  ocrError = signal<string | null>(null);
  ocrResult = signal<EmailOcrResult | null>(null);

  // PNG files after OCR processing
  generatedPngFiles = signal<EmailOcrPngFile[]>([]);
  showPngPreview = signal(false);

  // Computed properties
  canProcessWithOcr = computed(() => {
    const attachment = this.emailAttachment();
    if (!attachment.filename && !attachment.name) return false;

    const filename = (attachment.filename || attachment.name).toLowerCase();
    const supportedExtensions = ['.pdf', '.png', '.jpg', '.jpeg'];
    return supportedExtensions.some(ext => filename.endsWith(ext));
  });

  isPdfAttachment = computed(() => {
    const attachment = this.emailAttachment();
    const filename = (attachment.filename || attachment.name).toLowerCase();
    return filename.endsWith('.pdf');
  });

  attachmentSize = computed(() => {
    const attachment = this.emailAttachment();
    if (!attachment.size) return '';
    return this.formatFileSize(attachment.size);
  });

  attachmentIcon = computed(() => {
    const attachment = this.emailAttachment();
    const filename = (attachment.filename || attachment.name).toLowerCase();

    if (filename.endsWith('.pdf')) return 'picture_as_pdf';
    if (filename.match(/\.(jpg|jpeg|png|gif|bmp)$/)) return 'image';
    if (filename.match(/\.(doc|docx)$/)) return 'description';
    if (filename.match(/\.(xls|xlsx)$/)) return 'table_chart';
    return 'attach_file';
  });

  // PNG preview computed properties
  hasPngFiles = computed(() => this.generatedPngFiles().length > 0);

  // Display files (original attachment or PNG files if available)
  displayFiles = computed(() => {
    const pngFiles = this.generatedPngFiles();
    if (pngFiles.length > 0 && this.showPngPreview()) {
      // Convert PNG files to EmailAttachment format for display
      return pngFiles.map(png => ({
        name: png.filename,
        filename: png.filename,
        contentType: png.type,
        size: png.size,
        // Add PNG-specific data if needed
        id: png.id,
        location: png.location,
      }));
    }

    // Return original attachment
    return [this.emailAttachment()];
  });

  // Show preview button for PNG files
  canShowPreview = computed(() => {
    return this.hasPngFiles() && this.showPngPreview();
  });

  /**
   * Process attachment with OCR
   */
  processWithOcr(): void {
    const attachment = this.emailAttachment();

    if (!this.canProcessWithOcr() || this.isProcessingOcr()) {
      return;
    }

    //  If current attachment is already in previewd state, close it
    if (this.ocrPreviewStatus() === 'previewed') {
      this.onCloseOcrPreview.emit(attachment.id);

      if (this.pngPreviewMode() === 'new-application') {
        this.emailPreviewService.closePngPreviewNewApp();
      } else {
        this.showPngPreview.set(false);
        this.emailPreviewService.closePngPreview();
      }

      return;
    }

    // Try to get attachment data from the input first
    let attachmentData = attachment.data;

    // If data is missing, try to get it from the service's viewedEmail (lazy-loaded attachments)
    if (!attachmentData) {
      const viewedEmail = this.emailOAuthService.viewedEmail();
      const attachmentFilename = attachment.filename || attachment.name;

      if (viewedEmail?.Attachments) {
        const matchingAttachment = viewedEmail.Attachments.find(
          att => att.Filename === attachmentFilename
        );

        if (matchingAttachment?.Data) {
          attachmentData = matchingAttachment.Data;
        }
      }
    }

    if (!attachmentData) {
      this.ocrError.set(
        'Attachment data not available yet. Please wait for attachments to finish loading.'
      );
      console.warn('No attachment data available:', {
        filename: attachment.filename || attachment.name,
        hasDataInInput: !!attachment.data,
        viewedEmailHasAttachments: !!this.emailOAuthService.viewedEmail()?.Attachments,
      });
      return;
    }

    // Convert EmailAttachment to Attachment format
    const ocrAttachment: Attachment = {
      Filename: attachment.filename || attachment.name,
      ContentType: attachment.contentType || 'application/pdf',
      Data: attachmentData,
      Size: attachment.size || 0,
    };

    this.processOcrWithAttachment(ocrAttachment);
  }

  /**
   * Process OCR with the attachment (assumes attachment has Data property)
   */
  private processOcrWithAttachment(ocrAttachment: Attachment): void {
    if (!ocrAttachment.Data) {
      this.ocrError.set('No attachment data available for OCR processing');
      return;
    }

    const tenantId = this.authService.getUser().tenantId;

    this.ocrError.set(null);

    if (ocrAttachment.ContentType === 'application/pdf') {
      const ocrRequest$ = this.emailOcrService.previewEmailAttachment(ocrAttachment, tenantId).pipe(
        tap((result: EmailOcrResult) => {
          // Save OCR result locally after successful processing
          this.handleOcrSuccess(result);
        }),
        catchError(error => {
          const errorMessage =
            error?.error?.message || error?.message || error?.error || 'OCR processing failed';
          this.ocrError.set(errorMessage);
          console.error('OCR processing error:', {
            error,
            status: error?.status,
            statusText: error?.statusText,
            errorBody: error?.error,
            message: error?.message,
          });
          throw error;
        })
      );

      // Emit Observable to parent
      this.ocrPreviewRequest.emit(ocrRequest$);
    } else {
      const uploadRequest$ = this.uploadAttachmentToServer(ocrAttachment).pipe(
        tap((response: any) => {
          if (response) {
            const emailOcrPngFileModel: any = {
              id: response.id,
              filename: response.filename,
              type: response.type,
              size: response.size,
              location: response.location || '',
              category: response.category || 'generic',
              createdDate: response.createdDate || Date.now(),
            };

            this.handlePngSuccess(emailOcrPngFileModel);
          }
        })
      );
      this.ocrPreviewRequest.emit(uploadRequest$);
    }
  }
  /**
   * Create application from OCR result - navigate to add-application
   */
  createApplication(): void {
    const ocrResult = this.ocrResult();
    if (!ocrResult) {
      console.warn('No OCR result available for application creation');
      return;
    }

    // Navigate to add-application with OCR data
    this.emailApplicationBridge.createApplicationFromEmail(ocrResult);
  }

  /**
   * Handle successful OCR processing
   */
  private handleOcrSuccess(result: EmailOcrResult): void {
    // Save OCR result
    this.ocrResult.set(result);

    // Save PNG files for preview and automatically open preview column
    if (result.pngFiles && result.pngFiles.length > 0) {
      this.generatedPngFiles.set(result.pngFiles);
      this.showPngPreview.set(true);

      if (this.pngPreviewMode() === 'new-application') {
        this.emailPreviewService.openPngPreviewNewApp(result.pngFiles);
      } else {
        this.emailPreviewService.openPngPreview(result.pngFiles);
        this.emailLayoutService.openPreview();
      }
    }
  }
  /**
   * Handle successful PNG processing
   */
  private handlePngSuccess(result: any): void {
    if (result.id) {
      if (this.pngPreviewMode() === 'new-application') {
        this.emailPreviewService.openPngPreviewNewApp([result]);
      } else {
        this.emailPreviewService.openPngPreview([result]);
        this.emailLayoutService.openPreview();
      }
    }
  }

  /**
   * Download attachment
   */
  downloadAttachment(): void {
    const attachment = this.emailAttachment();

    if (!attachment.data) {
      console.warn('No attachment data available for download');
      return;
    }

    try {
      // Convert base64 to blob
      const base64 = attachment.data.replace(/^data:[^;]+;base64,/, '');
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], {
        type: attachment.contentType || 'application/octet-stream',
      });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.filename || attachment.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading attachment:', error);
    }
  }

  /**
   * Format file size for display
   */
  private formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${Math.round(bytes / (1024 * 1024))} MB`;
  }

  handleAttachStipulation($event: { type: string; data: string }) {
    switch ($event.type) {
      case 'change':
        this.attachedStipulations.update(current => {
          const _normalized = current || [];
          const _stipulationId = $event.data as any;
          const _attachmentId = this.emailAttachment().id;
          if (!_attachmentId) {
            return _normalized;
          }

          const _foundIndex = _normalized.findIndex(item => item.attachmentId === _attachmentId);
          if (!_stipulationId && _foundIndex !== -1) {
            _normalized.splice(_foundIndex, 1);
          } else if (_foundIndex !== -1) {
            _normalized[_foundIndex] = {
              attachmentId: _attachmentId,
              stipulationId: _stipulationId,
            };
          } else {
            _normalized.push({ attachmentId: _attachmentId, stipulationId: _stipulationId });
          }
          return [..._normalized];
        });
        break;
    }
  }

  /**
   * Upload a single attachment to the server
   */
  private uploadAttachmentToServer(attachment: any) {
    // Handle both lowercase and capitalized property names
    let attachmentData = attachment.data || attachment.Data;
    const attachmentFilename = attachment.filename || attachment.name || attachment.Filename;
    const attachmentContentType =
      attachment.contentType || attachment.ContentType || 'application/octet-stream';

    // If attachment data is missing (lazy loaded attachments might not have Data),
    // we need to fetch it from the backend using emailId and provider
    if (!attachmentData && this.emailId && this.provider) {
      console.warn(
        'Attachment data missing for lazy-loaded attachment. ' +
          'This attachment may need to be fetched with includeAttachments=true. ' +
          'Filename:',
        attachmentFilename
      );
      // Note: We can't easily fetch individual attachment data here without the attachment ID
      // The backend would need an endpoint like /emails/{emailId}/attachments/{attachmentId}/content
      // For now, we'll return an error observable
      console.error(
        'Cannot upload attachment without data. ' +
          'Attachment was likely lazy-loaded without base64 data. ' +
          'Please ensure attachments are loaded with includeAttachments=true before opening this dialog.'
      );
      return of(null);
    }

    if (!attachmentData) {
      console.error(
        'No data available for attachment:',
        attachmentFilename,
        'Available properties:',
        Object.keys(attachment)
      );
      return of(null);
    }

    if (!attachmentFilename) {
      console.error(
        'No filename available for attachment:',
        attachment,
        'Available properties:',
        Object.keys(attachment)
      );
      return of(null);
    }

    try {
      // Convert base64 to blob
      // Handle both data URL format and raw base64
      let base64 = attachmentData;
      if (base64.includes(',')) {
        // Remove data URL prefix if present (e.g., "data:application/pdf;base64,...")
        base64 = base64.split(',')[1];
      } else if (base64.startsWith('data:')) {
        base64 = base64.replace(/^data:[^;]+;base64,/, '');
      }

      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], {
        type: attachmentContentType,
      });

      // Create FormData for upload
      const formData = new FormData();
      formData.append('file', blob, attachmentFilename);

      const tenantId = this.authService.getUser().tenantId;

      if (!tenantId) {
        console.error('No tenant ID available');
        return of(null);
      }

      // Upload to server
      return this.attachmentService.upload(formData, 'application', 'generic', tenantId).pipe(
        tap((response: any) => {
          if (!response || (!response.id && !(Array.isArray(response) && response.length > 0))) {
            console.warn(`Upload response missing ID for ${attachmentFilename}:`, response);
          }
        }),
        catchError(error => {
          console.error(`Failed to upload ${attachmentFilename}:`, error);
          console.error('Full error object:', JSON.stringify(error, null, 2));
          console.error('Error details:', {
            status: error?.status,
            statusText: error?.statusText,
            message: error?.message,
            error: error?.error,
            url: error?.url,
            name: error?.name,
            stack: error?.stack,
          });

          return of(null);
        })
      );
    } catch (error: any) {
      console.error('Error preparing attachment for upload:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        attachment: {
          filename: attachmentFilename,
          hasData: !!attachmentData,
          dataType: typeof attachmentData,
        },
      });
      return of(null);
    }
  }
}
